/* Atlas v3 — "More": secondary destinations as a clean text list.

   Handoff override: the mockup's MoreSheet (a bottom-sheet with icon
   tiles) becomes a full ROUTED page here, and the icons are DROPPED per
   the spec ("clean list, no icon tiles"). Each row is label + optional
   sublabel + chevron, hairline-divided, no grouped card fills.

   Brings its own shell (TickerStrip + BottomNav) like the other v3
   screens — NOT wrapped in the old AppLayout. No starfield. */
import { useState } from 'react'
import { TickerCtx } from './lib'
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { v2Navigate } from '@/v2/lib/useV2Path'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

// Real route set — secondary destinations reachable from More. Each routes
// to a v3 screen via v2Navigate.
// Browse surfaces (Explore, Leverage Desk, Global Desk) moved onto the
// Markets tab (DeskSwitcher) in the tab consolidation — More is utilities only.
const ACTIVE = [
  { id: 'calendar', label: 'Calendar', sub: 'Upcoming earnings', href: '/calendar' },
  { id: 'insights', label: 'Insights', sub: 'The Weekly briefing', href: '/insights' },
  { id: 'cycle', label: 'Cycle', sub: 'AI cycle & supply-chain panel', href: '/cycle' },
  { id: 'settings', label: 'Settings', sub: 'Alerts, account & appearance', href: '/settings' },
]
const SOON = ['Lab', 'Reports', 'AI Universe', 'Capital Flow']

/* ── active row — hairline, label + sublabel + chevron, no icon tile ── */
function MoreRow({ item, first }) {
  const [hot, setHot] = useState(false)
  return (
    <button
      onClick={() => v2Navigate(item.href)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)}
      onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%',
        padding: '15px 20px', textAlign: 'left', cursor: 'pointer', border: 'none',
        background: hot ? 'hsl(var(--studio-surface-2))' : 'transparent',
        borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.35)',
        transition: 'background 140ms var(--studio-ease)',
      }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...Sans, fontSize: 14.5, transition: 'color 160ms', color: hot ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))' }}>{item.label}</div>
        {item.sub && <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>{item.sub}</div>}
      </div>
      <span style={{ color: 'hsl(var(--studio-accent-soft))', fontSize: 17, flexShrink: 0 }}>›</span>
    </button>
  )
}

/* ── coming-soon row — quiet, no chevron, no tap ── */
function SoonRow({ label, first }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '15px 20px', opacity: 0.5,
      borderTop: first ? 'none' : '1px solid hsl(var(--studio-border) / 0.35)',
    }}>
      <span style={{ ...Sans, fontSize: 14.5, color: 'hsl(var(--studio-text-muted))' }}>{label}</span>
      <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))' }}>COMING SOON</span>
    </div>
  )
}

function MoreBody() {
  const { openTicker } = useStockDetail()
  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        <div style={{ flexShrink: 0, padding: '20px 20px 4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...Display, fontSize: 32, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>More</div>
          </div>
          <SearchPill />
        </div>
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginTop: 14 }}>
          <div>
            {ACTIVE.map((item, i) => <MoreRow key={item.id} item={item} first={i === 0} />)}
          </div>
          <div style={{ height: 1, background: 'hsl(var(--studio-border) / 0.5)', margin: '22px 20px 0' }} />
          <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', padding: '20px 20px 4px' }}>ON THE ROADMAP</div>
          <div>
            {SOON.map((label, i) => <SoonRow key={label} label={label} first={i === 0} />)}
          </div>
          <div style={{ height: 16 }} />
        </div>
        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function More() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <MoreBody />
      </StockDetailProvider>
    </div>
  )
}
